import { motion } from "framer-motion";
import { Linkedin } from "lucide-react";
import { useSEO } from "../lib/seo";
import { fadeInUp, HEADSHOT_URL, CONTACT } from "../lib/brand";
import { SectionHeading } from "../components/site/Primitives";
import { PageHero, CTASection } from "../components/site/Sections";

const stagger = (i) => ({ ...fadeInUp, transition: { ...fadeInUp.transition, delay: 0.07 * i } });

const PRINCIPLES = [
  { title: "Practical, not theoretical", copy: "Shaped by what it actually takes to lead change inside a real organization." },
  { title: "Executive experience", copy: "Perspective from leading enterprise transformation and advising senior teams." },
  { title: "Large-scale transformation", copy: "Fluent in what changes when an effort spans an entire institution." },
  { title: "Leadership and change expertise", copy: "How leaders sponsor change, how people adopt it, and why efforts stall." },
  { title: "Complex institutions", copy: "Universities, nonprofits, cultural institutions, and mid-sized businesses—each with its own culture and governance." },
  { title: "Strategy, people, process, and technology—together", copy: "Transformation succeeds when these move as one, not as separate workstreams." },
];

const SERVE = [
  { group: "Institutions", items: ["Higher education institutions", "Nonprofit organizations", "Museums and cultural institutions", "Mid-sized businesses", "Complex organizations undergoing transformation"] },
  { group: "Leaders", items: ["Presidents and CEOs", "Provosts and Chief Strategy Officers", "CIOs, CHROs, and COOs", "Transformation executives", "Senior leadership teams"] },
];

export default function About() {
  useSEO(
    "About Summit Transformation Group",
    "Summit Transformation Group is a boutique AI strategy and organizational transformation advisory firm bringing real-world executive transformation experience into client engagements."
  );

  return (
    <>
      <PageHero
        eyebrow="About Summit"
        title="Executive transformation experience, brought into your organization."
        copy="Summit is a boutique advisory firm working with presidents, chief executives, provosts, and senior leadership teams to turn AI and transformation strategy into organizational performance."
        testId="about-hero"
      />

      <section data-testid="about-principles" className="py-24 md:py-32 bg-summit-bg border-t border-summit-border">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionHeading eyebrow="How We Work" title="A collaborative approach grounded in real-world leadership." className="mb-14" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-10">
            {PRINCIPLES.map((p, i) => (
              <motion.div key={p.title} {...stagger(i)} data-testid={`principle-${i}`} className="border-t border-summit-border pt-6">
                <span className="font-body text-xs tracking-[0.2em] text-summit-gold-dark">0{i + 1}</span>
                <h3 className="mt-3 font-heading text-2xl font-medium text-summit-charcoal leading-tight">{p.title}</h3>
                <p className="mt-3 font-body text-sm text-summit-text-muted leading-relaxed">{p.copy}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="founder" data-testid="about-founder" className="py-24 md:py-32 bg-summit-sand overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <motion.div {...stagger(0)} className="lg:col-span-4">
            <div className="relative max-w-sm">
              <div className="hidden sm:block absolute -inset-3 border border-summit-gold/50 translate-x-4 translate-y-4" />
              <img src={HEADSHOT_URL} alt="Jeremy Bean" className="relative w-full aspect-[4/5] object-cover object-top" />
            </div>
          </motion.div>
          <div className="lg:col-span-7 lg:col-start-6">
            <SectionHeading eyebrow="Founder & Principal" title="Jeremy Bean" />
            <motion.div {...stagger(2)} className="mt-8 space-y-5 font-body text-base md:text-lg text-summit-text-muted leading-relaxed">
              <p>
                Jeremy Bean is Assistant Vice President for Enterprise Change & Transformation at Penn State University, where he leads enterprise-wide efforts to help university leadership and operational units navigate complex, human-centered transformation.
              </p>
              <p>
                Over more than 20 years he has consulted and facilitated change across Fortune 500 companies, nonprofits, government agencies, and higher education. A Prosci-certified change practitioner, he serves on the Board of Directors of the Network for Change and Continuous Innovation (NCCI).
              </p>
            </motion.div>
            <motion.div {...stagger(3)} className="mt-8 flex flex-wrap items-center gap-3">
              {["Prosci Certified Change Practitioner", "NCCI Board of Directors", "20+ Years in Transformation"].map((t) => (
                <span key={t} className="px-3 py-1.5 border border-summit-gold/60 text-summit-charcoal text-xs font-body tracking-wide">{t}</span>
              ))}
              <a href={CONTACT.linkedin} target="_blank" rel="noopener noreferrer" data-testid="founder-linkedin-link" className="inline-flex items-center gap-2 text-sm font-body font-semibold text-summit-forest hover:text-summit-gold-dark transition-colors ml-1">
                <Linkedin size={16} /> LinkedIn
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      <section data-testid="about-serve" className="py-24 md:py-32 bg-summit-bg">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow="Who We Serve" title="Organizations and leaders navigating consequential change." copy="Summit works best with leadership teams who recognize that AI adoption and transformation are organizational undertakings—and want a partner who has led them." />
          </div>
          <div className="lg:col-span-6 lg:col-start-7 grid grid-cols-1 sm:grid-cols-2 gap-10">
            {SERVE.map((g, gi) => (
              <motion.div key={g.group} {...stagger(gi + 1)}>
                <p className="font-body text-xs uppercase tracking-[0.22em] text-summit-gold-dark font-semibold mb-5">{g.group}</p>
                <ul className="space-y-3">
                  {g.items.map((it) => (
                    <li key={it} className="flex items-start gap-3 font-body text-sm md:text-base text-summit-charcoal">
                      <span className="mt-2 w-1.5 h-1.5 rotate-45 bg-summit-gold shrink-0" /> {it}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CTASection heading="Let's talk about what your organization is trying to accomplish." primaryLabel="Talk With Summit" testId="about-cta" />
    </>
  );
}
